import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AjsResponse, wrapAjs } from '../ajs-response';
import { buildParams, PAGINATION_SEARCH_FIELDS } from '../http-params.util';

/**
 * RolesApiService (Angular 18) — RBAC roles + permission mappings.
 *
 * Replaces apps/b2b-ui/common/services/api/rolesApi/rolesApi.service.js.
 *
 * Two sub-namespaces:
 *   apiService.roles.permissions.list                role → permissions list
 *   apiService.roles.permissionMaps.{...}            CRUD on role/permission map
 *
 * Legacy quirk preserved: `create()` and `update()` send `permissions: "[]"`
 * as a literal string (not an array). The backend deserialises this on its
 * side; consumers don't see the wire shape.
 */
const ROLES_BASE_V1 = '/v1/api/rbac/roles';
const ROLES_BASE_V2 = '/v2/api/rbac/roles';
const ROLE_PERMISSION_MAP_BASE_V1 = '/v1/api/rbac/role/permission/map';
const ROLE_PERMISSION_MAP_BASE_V2 = '/v2/api/rbac/role/permission/map';

@Injectable({ providedIn: 'root' })
export class RolesApiService {
  private readonly http = inject(HttpClient);

  list(props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(ROLES_BASE_V2, {
        params: buildParams(props, PAGINATION_SEARCH_FIELDS),
      }),
    );
  }

  detail(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${ROLES_BASE_V1}/${id}`));
  }

  remove(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.delete<unknown>(`${ROLES_BASE_V1}/${id}`));
  }

  create(props: { name: string; active: boolean }): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(ROLES_BASE_V1, {
        name: props.name,
        permissions: '[]',
        active: props.active,
      }),
    );
  }

  update(props: {
    id: number | string;
    name: string;
    active: boolean;
    createdAt?: unknown;
  }): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(ROLES_BASE_V1, {
        id: props.id,
        name: props.name,
        permissions: '[]',
        active: props.active,
        createdAt: props.createdAt,
      }),
    );
  }

  readonly permissions = {
    list: (id: number | string, props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${ROLES_BASE_V2}/${id}/permissions`, {
          params: buildParams(props, PAGINATION_SEARCH_FIELDS),
        }),
      ),
  };

  readonly permissionMaps = {
    list: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(ROLE_PERMISSION_MAP_BASE_V2, {
          params: buildParams(props, PAGINATION_SEARCH_FIELDS),
        }),
      ),

    detail: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${ROLE_PERMISSION_MAP_BASE_V1}/${id}`)),

    remove: (props: { ids: Array<number | string> }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.request<unknown>('DELETE', ROLE_PERMISSION_MAP_BASE_V1, {
          body: props.ids,
        }),
      ),

    create: (props: { roleId: number | string; permissionId: number | string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(ROLE_PERMISSION_MAP_BASE_V1, [
          { roleId: props.roleId, permissionId: props.permissionId },
        ]),
      ),

    batchCreate: (props: { mappings: unknown }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.post<unknown>(ROLE_PERMISSION_MAP_BASE_V1, props.mappings)),

    update: (props: {
      id: number | string;
      roleId: number | string;
      permissionId: number | string;
    }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.put<unknown>(ROLE_PERMISSION_MAP_BASE_V1, {
          id: props.id,
          roleId: props.roleId,
          permissionId: props.permissionId,
        }),
      ),
  };
}
